/**
 * A QR encoder, written here rather than taken from npm.
 *
 * The confirmation slip's QR is the check-in credential and the `/manage/{token}` key (D21), so it
 * has to render server-side, inline, with no client JavaScript and no third-party code in the path
 * of a participant's token. Every published encoder is a dependency that would be pulled into the
 * Worker bundle for one function; this is that one function.
 *
 * **It is its own package because three surfaces need it and none of them can depend on the others.**
 * The community site prints the participant's slip, the management app prints the event's poster
 * codes, and the API builds the URL that goes on both. It lived in `@powwow/ui` first, which the
 * website cannot import — `ui` is React and Radix, and the site ships neither — so a second copy was
 * made there, and the two drifted: they were six hundred near-identical lines with different
 * handling of `noUncheckedIndexedAccess` and one comment that no longer described where the file
 * was. Two encoders are two chances to be subtly wrong about something a camera either reads or
 * does not, which is exactly the failure nobody notices until a queue forms at a gate.
 *
 * So it is built to the same rule as `domain` and `validation`: no React, no DOM, no Node, compiled
 * against `tsconfig.agnostic.json` so a native build could use it unchanged (D2). A consumer that
 * wants only this pays for only this.
 *
 * **Rendering that is not a string stays out.** `qrSvg` belongs here because an SVG is text and
 * every target can build one. A PNG is not — it needs `CompressionStream`, which Workers and
 * browsers have and React Native does not — so the one place that needs bytes
 * (`apps/website/src/lib/qr-png.ts`, for the confirmation email) builds them there, on top of the
 * module grid `encodeQr` returns.
 *
 * Scope is deliberately narrow, and narrowness is what makes it reviewable:
 *
 * - **Byte mode only.** URLs are what we encode. Alphanumeric mode would be denser for an
 *   upper-cased URL, but a manage token is case-sensitive and mixed-case, so the mode never applies.
 * - **Error correction level M only** (~15% recovery). L would fit more in a smaller symbol; M is
 *   the level that survives a slip that has been folded into a pocket and rained on, which is the
 *   actual operating condition (ux.md §2).
 * - **Versions 1–10**, up to 213 bytes. A manage URL is around 70 characters. Versions beyond 10
 *   need a larger block table and a second character-count width, and encoding a novel is not a
 *   thing this system does.
 *
 * The output is an inline SVG. It is black on white rather than themed: a QR symbol is read by a
 * camera, and a brand colour that happened to be mid-grey would produce a code that looks right
 * and scans nowhere. That is the one place in this app where a colour is not a token.
 *
 * References: ISO/IEC 18004. The block structure, alignment-pattern centres and remainder-bit
 * counts below are that standard's tables, transcribed for versions 1–10 at level M.
 */

/* ------------------------------------------------------------------ tables */

/**
 * Per version: error-correction codewords per block, and the block structure as
 * `[blockCount, dataCodewordsPerBlock]` groups. Indexed by version, so slot 0 is unused.
 *
 * The invariant worth checking when reading this: for every row,
 * `sum(count * (data + ec)) === total codewords for that version` — 26, 44, 70, 100, 134, 172,
 * 196, 242, 292, 346.
 */
const BLOCKS: ReadonlyArray<{ ec: number; groups: ReadonlyArray<readonly [number, number]> }> = [
  { ec: 0, groups: [] }, // version 0 does not exist
  { ec: 10, groups: [[1, 16]] },
  { ec: 16, groups: [[1, 28]] },
  { ec: 26, groups: [[1, 44]] },
  { ec: 18, groups: [[2, 32]] },
  { ec: 24, groups: [[2, 43]] },
  { ec: 16, groups: [[4, 27]] },
  { ec: 18, groups: [[4, 31]] },
  {
    ec: 22,
    groups: [
      [2, 38],
      [2, 39],
    ],
  },
  {
    ec: 22,
    groups: [
      [3, 36],
      [2, 37],
    ],
  },
  {
    ec: 26,
    groups: [
      [4, 43],
      [1, 44],
    ],
  },
];

/** Alignment-pattern centre coordinates per version. Version 1 has none. */
const ALIGNMENT: ReadonlyArray<readonly number[]> = [
  [],
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

/** Bits left over after the codewords, which are filled with zeros. */
const REMAINDER_BITS: readonly number[] = [0, 0, 7, 7, 7, 7, 7, 0, 0, 0, 0];

/** Error-correction level M, as the two bits that go into the format information. */
const EC_LEVEL_BITS = 0b00;

export const MAX_VERSION = BLOCKS.length - 1;

/* ------------------------------------------------------------- GF(256) math */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

{
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    // Primitive polynomial x^8 + x^4 + x^3 + x^2 + 1, as the standard specifies.
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255]!;
}

function gfMultiply(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}

/** The generator polynomial for `degree` error-correction codewords: ∏(x − α^i). */
function generatorPolynomial(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i += 1) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j += 1) {
      // Written out rather than `^=`, because this package compiles with `noUncheckedIndexedAccess`
      // and a compound assignment reads the element before it writes it.
      next[j] = next[j]! ^ poly[j]!;
      next[j + 1] = next[j + 1]! ^ gfMultiply(poly[j]!, EXP[i]!);
    }
    poly = next;
  }
  return poly;
}

/** Reed–Solomon remainder — the error-correction codewords for one block. */
export function errorCorrectionCodewords(data: Uint8Array, ecLength: number): Uint8Array {
  const generator = generatorPolynomial(ecLength);
  const working = new Uint8Array(data.length + ecLength);
  working.set(data);

  for (let i = 0; i < data.length; i += 1) {
    const factor = working[i]!;
    if (factor === 0) continue;
    // `generator[0]` is 1, so this step always clears `working[i]`.
    for (let j = 0; j < generator.length; j += 1) {
      working[i + j] = working[i + j]! ^ gfMultiply(generator[j]!, factor);
    }
  }

  return working.slice(data.length);
}

/* ------------------------------------------------------------------ encode */

/** UTF-8 bytes, written out rather than taken from `TextEncoder` so this file stays pure data. */
function utf8Bytes(text: string): number[] {
  const bytes: number[] = [];
  for (const character of text) {
    const point = character.codePointAt(0)!;
    if (point < 0x80) {
      bytes.push(point);
    } else if (point < 0x800) {
      bytes.push(0xc0 | (point >> 6), 0x80 | (point & 0x3f));
    } else if (point < 0x10000) {
      bytes.push(0xe0 | (point >> 12), 0x80 | ((point >> 6) & 0x3f), 0x80 | (point & 0x3f));
    } else {
      bytes.push(
        0xf0 | (point >> 18),
        0x80 | ((point >> 12) & 0x3f),
        0x80 | ((point >> 6) & 0x3f),
        0x80 | (point & 0x3f),
      );
    }
  }
  return bytes;
}

function dataCodewordCount(version: number): number {
  return BLOCKS[version]!.groups.reduce((total, [count, size]) => total + count * size, 0);
}

/** Bits the character count takes in byte mode: 8 up to version 9, 16 from version 10. */
function characterCountBits(version: number): number {
  return version < 10 ? 8 : 16;
}

/** The smallest version that holds `byteLength` bytes, or undefined if none of 1–10 does. */
export function chooseVersion(byteLength: number): number | undefined {
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    const capacity = dataCodewordCount(version) * 8 - 4 - characterCountBits(version);
    if (byteLength * 8 <= capacity) return version;
  }
  return undefined;
}

/** Mode indicator, character count, payload, terminator, padding — as one codeword array. */
function dataCodewords(bytes: readonly number[], version: number): Uint8Array {
  const bits: number[] = [];
  const push = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >> i) & 1);
  };

  push(0b0100, 4); // byte mode
  push(bytes.length, characterCountBits(version));
  for (const byte of bytes) push(byte, 8);

  const capacity = dataCodewordCount(version) * 8;
  // Terminator: up to four zero bits, fewer if the symbol is nearly full.
  push(0, Math.min(4, capacity - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords = new Uint8Array(dataCodewordCount(version));
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) byte = (byte << 1) | bits[i + j]!;
    codewords[i / 8] = byte;
  }

  // Pad bytes alternate 0xEC / 0x11, which is what the standard specifies rather than zeros.
  for (let i = bits.length / 8; i < codewords.length; i += 1) {
    codewords[i] = (i - bits.length / 8) % 2 === 0 ? 0xec : 0x11;
  }

  return codewords;
}

/**
 * Split into blocks, compute each block's ECC, and interleave.
 *
 * Interleaving is what makes the level-M promise real: a coffee ring covering one corner damages a
 * few codewords of every block rather than destroying one block entirely.
 */
function interleave(codewords: Uint8Array, version: number): Uint8Array {
  const { ec, groups } = BLOCKS[version]!;

  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;
  for (const [count, size] of groups) {
    for (let i = 0; i < count; i += 1) {
      const block = codewords.slice(offset, offset + size);
      offset += size;
      dataBlocks.push(block);
      ecBlocks.push(errorCorrectionCodewords(block, ec));
    }
  }

  const out: number[] = [];
  const longest = Math.max(...dataBlocks.map((block) => block.length));
  for (let i = 0; i < longest; i += 1) {
    for (const block of dataBlocks) {
      if (i < block.length) out.push(block[i]!);
    }
  }
  for (let i = 0; i < ec; i += 1) {
    for (const block of ecBlocks) out.push(block[i]!);
  }

  return Uint8Array.from(out);
}

/* ------------------------------------------------------------------ matrix */

type Grid = boolean[][];

function blankGrid(size: number): Grid {
  return Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
}

interface Canvas {
  readonly size: number;
  readonly modules: Grid;
  /** A function module — finder, timing, alignment, format, version. Never masked, never data. */
  readonly reserved: Grid;
}

function setFunctionModule(canvas: Canvas, row: number, column: number, dark: boolean): void {
  canvas.modules[row]![column] = dark;
  canvas.reserved[row]![column] = true;
}

function drawFinder(canvas: Canvas, row: number, column: number): void {
  // The 8×8 block includes the one-module separator, which is light and also a function module.
  for (let dy = -1; dy <= 7; dy += 1) {
    for (let dx = -1; dx <= 7; dx += 1) {
      const y = row + dy;
      const x = column + dx;
      if (y < 0 || y >= canvas.size || x < 0 || x >= canvas.size) continue;
      // Distance from the centre: 0–1 is the dark core, 2 is the light ring, 3 is the dark ring,
      // 4 is the separator and stays light.
      const distance = Math.max(Math.abs(dy - 3), Math.abs(dx - 3));
      setFunctionModule(canvas, y, x, distance !== 2 && distance !== 4);
    }
  }
}

function drawAlignment(canvas: Canvas, row: number, column: number): void {
  for (let dy = -2; dy <= 2; dy += 1) {
    for (let dx = -2; dx <= 2; dx += 1) {
      const distance = Math.max(Math.abs(dy), Math.abs(dx));
      setFunctionModule(canvas, row + dy, column + dx, distance !== 1);
    }
  }
}

function drawFunctionPatterns(canvas: Canvas, version: number): void {
  const { size } = canvas;

  drawFinder(canvas, 0, 0);
  drawFinder(canvas, 0, size - 7);
  drawFinder(canvas, size - 7, 0);

  for (let i = 8; i < size - 8; i += 1) {
    setFunctionModule(canvas, 6, i, i % 2 === 0);
    setFunctionModule(canvas, i, 6, i % 2 === 0);
  }

  const centres = ALIGNMENT[version]!;
  for (const row of centres) {
    for (const column of centres) {
      // The three that would sit on a finder pattern are omitted.
      const onFinder =
        (row === 6 && column === 6) ||
        (row === 6 && column === size - 7) ||
        (row === size - 7 && column === 6);
      if (!onFinder) drawAlignment(canvas, row, column);
    }
  }

  // Reserve the format-information strips. Their contents depend on the mask, chosen later.
  for (let i = 0; i <= 8; i += 1) {
    if (i !== 6) {
      setFunctionModule(canvas, 8, i, false);
      setFunctionModule(canvas, i, 8, false);
    }
  }
  for (let i = 0; i < 8; i += 1) {
    setFunctionModule(canvas, 8, size - 1 - i, false);
    setFunctionModule(canvas, size - 1 - i, 8, false);
  }

  if (version >= 7) drawVersionInformation(canvas, version);
}

/** 18-bit BCH-protected version number, in the two blocks flanking the top-right and bottom-left. */
function drawVersionInformation(canvas: Canvas, version: number): void {
  let remainder = version;
  for (let i = 0; i < 12; i += 1) {
    remainder = (remainder << 1) ^ ((remainder >> 11) * 0x1f25);
  }
  const bits = (version << 12) | remainder;

  for (let i = 0; i < 18; i += 1) {
    const dark = ((bits >> i) & 1) !== 0;
    const a = canvas.size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    setFunctionModule(canvas, b, a, dark);
    setFunctionModule(canvas, a, b, dark);
  }
}

/** 15-bit BCH-protected format information, written twice so either copy can be lost. */
function drawFormatInformation(canvas: Canvas, mask: number): void {
  const data = (EC_LEVEL_BITS << 3) | mask;
  let remainder = data;
  for (let i = 0; i < 10; i += 1) {
    remainder = (remainder << 1) ^ ((remainder >> 9) * 0x537);
  }
  const bits = ((data << 10) | remainder) ^ 0x5412;
  const bit = (index: number) => ((bits >> index) & 1) !== 0;

  const { size } = canvas;

  for (let i = 0; i <= 5; i += 1) setFunctionModule(canvas, i, 8, bit(i));
  setFunctionModule(canvas, 7, 8, bit(6));
  setFunctionModule(canvas, 8, 8, bit(7));
  setFunctionModule(canvas, 8, 7, bit(8));
  for (let i = 9; i < 15; i += 1) setFunctionModule(canvas, 8, 14 - i, bit(i));

  for (let i = 0; i < 8; i += 1) setFunctionModule(canvas, 8, size - 1 - i, bit(i));
  for (let i = 8; i < 15; i += 1) setFunctionModule(canvas, size - 15 + i, 8, bit(i));

  // The dark module. Always set, in every symbol, at (4·version + 9, 8).
  setFunctionModule(canvas, size - 8, 8, true);
}

/** The zig-zag walk: two columns at a time, upward then downward, skipping the timing column. */
export function placementOrder(size: number, reserved: Grid): Array<[number, number]> {
  const order: Array<[number, number]> = [];
  let upward = true;

  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // column 6 is the vertical timing pattern
    for (let step = 0; step < size; step += 1) {
      for (let offset = 0; offset < 2; offset += 1) {
        const column = right - offset;
        const row = upward ? size - 1 - step : step;
        if (!reserved[row]![column]) order.push([row, column]);
      }
    }
    upward = !upward;
  }

  return order;
}

export const MASK_PATTERNS: ReadonlyArray<(row: number, column: number) => boolean> = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

/* ----------------------------------------------------------------- penalty */

const FINDER_RUN = [true, false, true, true, true, false, true];

function penalty(modules: Grid): number {
  const size = modules.length;
  let score = 0;

  const lines: boolean[][] = [];
  for (let i = 0; i < size; i += 1) {
    lines.push(modules[i]!.slice());
    lines.push(modules.map((row) => row[i]!));
  }

  // Rule 1 — runs of five or more.
  for (const line of lines) {
    let run = 1;
    for (let i = 1; i < line.length; i += 1) {
      if (line[i] === line[i - 1]) {
        run += 1;
        if (run === 5) score += 3;
        else if (run > 5) score += 1;
      } else {
        run = 1;
      }
    }
  }

  // Rule 2 — 2×2 blocks of one colour.
  for (let row = 0; row < size - 1; row += 1) {
    for (let column = 0; column < size - 1; column += 1) {
      const value = modules[row]![column];
      if (
        value === modules[row]![column + 1] &&
        value === modules[row + 1]![column] &&
        value === modules[row + 1]![column + 1]
      ) {
        score += 3;
      }
    }
  }

  // Rule 3 — a finder-like sequence with four light modules on either side.
  for (const line of lines) {
    for (let i = 0; i + 10 < line.length; i += 1) {
      const finder = FINDER_RUN.every((value, offset) => line[i + offset] === value);
      if (!finder) continue;
      const before = i >= 4 && line.slice(i - 4, i).every((value) => !value);
      const after = line.slice(i + 7, i + 11).every((value) => !value);
      if (before || after) score += 40;
    }
  }

  // Rule 4 — deviation from an even balance of dark and light.
  const dark = modules.reduce(
    (total, row) => total + row.reduce((count, value) => count + (value ? 1 : 0), 0),
    0,
  );
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/* ------------------------------------------------------------------- public */

export interface QrCode {
  readonly version: number;
  readonly size: number;
  readonly mask: number;
  /** Row-major, `true` for a dark module. Does not include the quiet zone. */
  readonly modules: ReadonlyArray<readonly boolean[]>;
}

export class QrTooLongError extends Error {
  constructor(byteLength: number) {
    super(`${byteLength} bytes is more than a version-${MAX_VERSION} symbol holds.`);
    this.name = "QrTooLongError";
  }
}

/**
 * Encode text as a QR symbol.
 *
 * The mask is chosen by scoring all eight, which is what the standard requires and what keeps a
 * symbol from containing a run that a scanner mistakes for a finder pattern.
 */
export function encodeQr(text: string): QrCode {
  const bytes = utf8Bytes(text);
  const version = chooseVersion(bytes.length);
  if (version === undefined) throw new QrTooLongError(bytes.length);

  const size = version * 4 + 17;
  const canvas: Canvas = { size, modules: blankGrid(size), reserved: blankGrid(size) };
  drawFunctionPatterns(canvas, version);

  const codewords = interleave(dataCodewords(bytes, version), version);
  const order = placementOrder(size, canvas.reserved);

  // The walk is exactly the codewords plus this version's remainder bits, which stay light. If
  // that ever stops being true the tables above and the matrix have drifted apart.
  const dataBits = codewords.length * 8;
  if (order.length !== dataBits + REMAINDER_BITS[version]!) {
    throw new Error(`QR version ${version}: ${order.length} module slots for ${dataBits} bits`);
  }

  for (let index = 0; index < order.length; index += 1) {
    const [row, column] = order[index]!;
    canvas.modules[row]![column] =
      index < dataBits && ((codewords[index >> 3]! >> (7 - (index & 7))) & 1) !== 0;
  }

  let best: { mask: number; modules: Grid; score: number } | undefined;
  for (let mask = 0; mask < MASK_PATTERNS.length; mask += 1) {
    const candidate: Canvas = {
      size,
      modules: canvas.modules.map((row) => row.slice()),
      reserved: canvas.reserved.map((row) => row.slice()),
    };
    const rule = MASK_PATTERNS[mask]!;
    for (let row = 0; row < size; row += 1) {
      for (let column = 0; column < size; column += 1) {
        if (!candidate.reserved[row]![column] && rule(row, column)) {
          candidate.modules[row]![column] = !candidate.modules[row]![column];
        }
      }
    }
    drawFormatInformation(candidate, mask);

    const score = penalty(candidate.modules);
    if (!best || score < best.score) best = { mask, modules: candidate.modules, score };
  }

  return { version, size, mask: best!.mask, modules: best!.modules };
}

export interface QrSvgOptions {
  /** Light modules around the symbol. Four is the standard minimum and what scanners expect. */
  quietZone?: number;
  /** Rendered width and height. The symbol scales to fit; the viewBox stays in module units. */
  pixelSize?: number;
  /** Accessible name. The URL itself is the sensible value, since that is what the code says. */
  title?: string;
}

/**
 * Render as an inline SVG string.
 *
 * Black on white, deliberately and not from a token: a camera reads this, and a brand colour that
 * happened to be mid-tone would make a symbol that looks right and scans nowhere. `print-color-
 * adjust` keeps a browser from helpfully dropping the background when the slip is printed.
 */
export function qrSvg(text: string, options: QrSvgOptions = {}): string {
  const { modules, size } = encodeQr(text);
  const quiet = options.quietZone ?? 4;
  const extent = size + quiet * 2;
  const pixels = options.pixelSize ?? 200;

  // One path for every dark module, merged along each row so the markup stays small.
  const parts: string[] = [];
  for (let row = 0; row < size; row += 1) {
    let column = 0;
    while (column < size) {
      if (!modules[row]![column]) {
        column += 1;
        continue;
      }
      let run = 1;
      while (column + run < size && modules[row]![column + run]) run += 1;
      parts.push(`M${column + quiet} ${row + quiet}h${run}v1h-${run}z`);
      column += run;
    }
  }

  const title = options.title ? `<title>${escapeXml(options.title)}</title>` : "";
  const label = options.title ? ` aria-label="${escapeXml(options.title)}"` : "";

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${extent} ${extent}" ` +
    `width="${pixels}" height="${pixels}" role="img"${label} ` +
    `shape-rendering="crispEdges" style="print-color-adjust:exact;-webkit-print-color-adjust:exact">` +
    `${title}<rect width="${extent}" height="${extent}" fill="#ffffff"/>` +
    `<path fill="#000000" d="${parts.join("")}"/></svg>`
  );
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
